import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, FileText, Inbox, Users, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const STAGES = ["Submitted", "In Review", "Quoted", "Paid", "Closed"];

export default function AdminDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="text-zinc-400 py-20 text-center">Loading...</div>;

  const cards = [
    { label: "Total Requests", value: d.total_requests, icon: ClipboardList },
    { label: "Open Quotes", value: d.open_quotes, icon: FileText, accent: true },
    { label: "Open Inquiries", value: d.open_inquiries, icon: Inbox },
    { label: "Suppliers", value: d.suppliers, icon: Users },
  ];
  const maxStage = Math.max(...STAGES.map((s) => d.status_counts[s] || 0), 1);

  return (
    <div className="max-w-6xl">
      <p className="text-coral text-xs font-bold uppercase tracking-widest mb-2">Admin Portal</p>
      <h1 className="font-serif text-4xl font-bold tracking-tight">Dashboard</h1>
      <p className="text-zinc-500 mt-2 mb-8">Operations overview at a glance.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className={`border rounded-sm p-5 ${c.accent ? "bg-coral text-white border-coral" : "bg-white border-zinc-200"}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon size={22} className={c.accent ? "text-white/80" : "text-coral"} />
            <div className="font-serif text-3xl font-bold mt-4">{c.value}</div>
            <div className={`text-xs uppercase tracking-wider mt-1 ${c.accent ? "text-white/70" : "text-zinc-500"}`}>{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-serif text-lg font-bold mb-5">Requests by stage</h3>
          <div className="space-y-3">
            {STAGES.map((s) => (
              <div key={s} className="flex items-center gap-3" data-testid={`stage-bar-${s.replace(/\s/g, "-")}`}>
                <div className="w-20 text-xs font-semibold text-zinc-500">{s}</div>
                <div className="flex-1 bg-zinc-100 h-6 rounded-sm overflow-hidden">
                  <div className="h-full bg-ink transition-all" style={{ width: `${((d.status_counts[s] || 0) / maxStage) * 100}%` }} />
                </div>
                <div className="w-8 text-right text-sm font-bold">{d.status_counts[s] || 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-zinc-100 text-sm text-zinc-500">
            <span className="font-semibold text-ink">{d.open_quotes}</span> quotes awaiting client response
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-lg font-bold">Recent requests</h3>
            <Link to="/admin/requests" className="text-coral text-sm font-semibold inline-flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          {d.recent_requests.length === 0 ? (
            <p className="text-zinc-400 text-sm py-8 text-center">No requests yet.</p>
          ) : (
            <div className="divide-y divide-zinc-100">
              {d.recent_requests.map((r) => (
                <Link key={r.id} to={`/admin/requests/${r.id}`} className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded-sm transition" data-testid={`recent-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{r.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{r.client_name}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
